import { createModelWalkthrough, CREATE_MODEL_WALKTHROUGH_ID } from './createModelWalkthrough';
import {
    taskManagementWalkthrough,
    TASK_MANAGEMENT_PAGE,
    TASK_MANAGEMENT_PAGE_ORDER,
    TASK_MANAGEMENT_WALKTHROUGH_ID,
    TASK_MANAGEMENT_WELCOME_CTA,
} from './taskManagementWalkthrough';

/**
 * Every walkthrough the platform knows, keyed by the id persisted in
 * `user.preferences.walkthroughs` (see `WalkthroughId` in optune-utils).
 * Adding one = one entry here plus a `WalkthroughRunner` mounted on the page
 * that hosts it. An id a user document still carries from an earlier build
 * (the retired per-page `wizard_*` guides) is simply unknown here: the
 * provider keeps its state untouched and no runner ever picks it up.
 *
 * Definition shape: `{ id, steps, requiredContext, style, ephemeral }`
 * - `style`      `'tour'` (blue bubble, Skip + counter, auto-advances) or
 *                `'guide'` (header X, counter, Skip + Next / Got it buttons)
 * - `ephemeral`  never persisted — the demo tour starts over on reload
 */
export const WALKTHROUGHS = Object.freeze({
    [CREATE_MODEL_WALKTHROUGH_ID]: createModelWalkthrough,
    [TASK_MANAGEMENT_WALKTHROUGH_ID]: taskManagementWalkthrough,
});

export const WALKTHROUGH_STYLE = Object.freeze({ tour: 'tour', guide: 'guide' });

export const WALKTHROUGH_STATUS = Object.freeze({
    inProgress: 'in_progress',
    chosenJump: 'chosen_jump',
    completed: 'completed',
    dismissed: 'dismissed',
});

/**
 * Persisted `step_id` once the pointer passed the last step but the tour is
 * still `in_progress` (the "You're all set" modal waits for the wizard to
 * finish). A reload then resumes past the end instead of replaying the tour.
 */
export const WALKTHROUGH_DONE_STEP_ID = '__done__';

export const getWalkthrough = (id) => WALKTHROUGHS[id] || null;

export const walkthroughStyle = (definition) => definition?.style || WALKTHROUGH_STYLE.tour;

export const isEphemeralWalkthrough = (definition) => definition?.ephemeral === true;

/** Registry id to persist for a pointer position: a step id, the done sentinel, or `null`. */
export const stepIdAt = (definition, stepIndex) => {
    const steps = definition?.steps || [];
    if (stepIndex >= steps.length && steps.length > 0) return WALKTHROUGH_DONE_STEP_ID;
    return steps[stepIndex]?.id ?? null;
};

/** Pointer position for a persisted `step_id`; `0` when unknown. */
export const stepIndexOf = (definition, stepId) => {
    const steps = definition?.steps || [];
    if (stepId === WALKTHROUGH_DONE_STEP_ID) return steps.length;
    const index = stepId ? steps.findIndex((s) => s.id === stepId) : -1;
    return index >= 0 ? index : 0;
};

/** Step selector: explicit `selector` wins, else the `data-walkthrough` attribute. */
export const stepSelector = (step) =>
    step.selector || `[data-walkthrough="${step.target}"]`;

/** Spark Tooltip `tipDirection` for a step's bubble placement (bubble side + tip alignment). */
export const tipDirectionFor = (placement = 'bottom', align = 'center') => {
    if (placement === 'left' || placement === 'right') return `${placement}-center`;
    const side = { start: 'left', end: 'right' }[align] || 'center';
    return `${placement}-${side}`;
};

export const stepTipDirection = (step) => tipDirectionFor(step.placement, step.align);

export const isStepApplicable = (step, ctx) =>
    typeof step.isApplicable === 'function' ? step.isApplicable(ctx || {}) !== false : true;

export const isStepComplete = (step, ctx) =>
    typeof step.isComplete === 'function' ? step.isComplete(ctx || {}) === true : false;

/** Centered bubbles (a welcome screen) have no anchor: `centered: true`, no `target`. */
export const isCenteredStep = (step) => step?.centered === true;

/** Steps counted in "Step N/Total" (`counted: false` opts a step out, e.g. a welcome bubble). */
export const isCountedStep = (step) => step?.counted !== false;

export {
    CREATE_MODEL_WALKTHROUGH_ID,
    TASK_MANAGEMENT_WALKTHROUGH_ID,
    TASK_MANAGEMENT_PAGE,
    TASK_MANAGEMENT_PAGE_ORDER,
    TASK_MANAGEMENT_WELCOME_CTA,
};
